import React, { useState } from 'react';
import AccessoriesToolbar from './AccessoriesToolbar';
import { ConfigContext, Grid } from 'src/base-ui/lib';
import AddAccessory from './AddAccessory';
import { Link, Stack, styled } from '@mui/material';
import EmptySearchState from 'src/pages/quotations/components/EmptySearchState';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EditAccessory from './EditAccessory';
import Accessory from 'src/types/shared/accessory';
import { DeleteAccessoryModal } from './DeleteAccessoryModal';

const StyledGridActionsCellItem = styled(GridActionsCellItem)(({ theme }) => ({
	color: theme.palette.error.main,
	'& .MuiTypography-root': {
		color: theme.palette.error.main,
	},
}));

const Accessories: React.FC = () => {
	const gridRef = React.useRef<any>(null);

	const { config } = React.useContext<any>(ConfigContext);
	const [searchInput, setSearchInput] = useState('');
	const [addAccessoryModalOpen, setAddAccessoryModalOpen] = useState(false);
	const [editAccessoryModalOpen, setEditAccessoryModalOpen] = useState(false);
	const [deleteAccessoryModalOpen, setDeleteAccessoryModalOpen] =
		useState(false);
	const [selectedRow, setSelectedRow] = useState<Accessory | null>(null);
	const [panelKey, setPanelKey] = useState(Date.now());

	const headCells = [
		{
			field: 'name',
			headerName: 'Accessory Name',
			renderCell: ({ row }) => {
				return (
					<Link
						component={RouterLink}
						to={''}
						underline="none"
						sx={{
							cursor: 'pointer',
							fontWeight: 'bold',
						}}
						onClick={() => {
							setEditAccessoryModalOpen(true);
							setSelectedRow(row);
						}}
					>
						{row.name}
					</Link>
				);
			},
			sortable: true,
			flex: 1.5,
		},
		{
			field: 'description',
			headerName: 'Description',
			sortable: true,
			flex: 2,
		},
		{
			field: 'price',
			headerName: 'Base Cost',
			sortable: true,
			flex: 1,
		},
		{
			field: 'calculationType',
			headerName: 'Calculation',
			sortable: true,
			flex: 1,
		},

		{
			field: 'actions',
			type: 'actions',
			getActions: ({ row }) => [
				<GridActionsCellItem
					key="edit"
					label="Edit"
					onClick={() => {
						setEditAccessoryModalOpen(true);
						setSelectedRow(row);
					}}
					showInMenu
					icon={<EditIcon />}
				/>,

				<StyledGridActionsCellItem
					key="delete"
					label="Delete"
					onClick={() => {
						setDeleteAccessoryModalOpen(true);
						setSelectedRow(row);
					}}
					showInMenu
					icon={<DeleteOutlineIcon sx={{ color: 'error.main' }} />}
				/>,
			],
			flex: 0.5,
		},
	];

	const gridOptions = {
		filter: searchInput,
	};
	return (
		<Stack>
			<AccessoriesToolbar
				searchInput={searchInput}
				setSearchInput={setSearchInput}
				onRefresh={() => gridRef.current?.onRefresh()}
				onAddAccessory={() => {
					setAddAccessoryModalOpen(true);
					setPanelKey(Date.now());
				}}
			/>

			<Grid
				key={'grid-accessories'}
				ref={gridRef}
				headCells={headCells}
				searchEndpont={`${config.uri.base}/accessory`}
				pageSizeOptions={[10, 25, 50]}
				selectableRows={false}
				initialPageSize={10}
				customNoRowsOverlay={EmptySearchState}
				hybridMode={true}
				options={gridOptions}
			/>

			{addAccessoryModalOpen && (
				<AddAccessory
					key={panelKey}
					isOpen={addAccessoryModalOpen}
					onClose={() => setAddAccessoryModalOpen(false)}
					onRefresh={() => gridRef.current?.onRefresh()}
				/>
			)}

			{selectedRow && (
				<EditAccessory
					accessory={selectedRow}
					isOpen={editAccessoryModalOpen}
					onRefresh={() => gridRef.current?.onRefresh()}
					onClose={() => {
						setEditAccessoryModalOpen(false);
						setSelectedRow(null);
					}}
				/>
			)}

			{selectedRow && (
				<DeleteAccessoryModal
					accessory={selectedRow}
					modalOpen={deleteAccessoryModalOpen}
					onRefresh={() => gridRef.current?.onRefresh()}
					onClose={() => {
						setDeleteAccessoryModalOpen(false);
						setSelectedRow(null);
					}}
				/>
			)}
		</Stack>
	);
};

export default Accessories;
